import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import api from '../../services/api';
import { itineraryService } from '../../services/itinerary';

interface AssignmentCompletionWizardProps {
  assignment: any;
  availableItineraries: any[];
  onClose: () => void;
  onSuccess: () => void;
}

const AssignmentCompletionWizard: React.FC<AssignmentCompletionWizardProps> = ({
  assignment,
  availableItineraries,
  onClose,
  onSuccess
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedItineraries, setSelectedItineraries] = useState<number[]>([]);
  const [linkedItineraries, setLinkedItineraries] = useState<any[]>([]);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredItineraries, setFilteredItineraries] = useState(availableItineraries);
  const [completionNotes, setCompletionNotes] = useState('');

  // Load saved selection from session storage on mount
  useEffect(() => {
    const sessionKey = `assignment_${assignment.id}_selected_itineraries`;
    const savedSelection = sessionStorage.getItem(sessionKey);
    if (savedSelection) {
      const savedIds = JSON.parse(savedSelection);
      setSelectedItineraries(savedIds);
      console.log('Loaded selected IDs from session:', savedIds);
    }
  }, [assignment.id]);

  const handleSearch = () => {
    if (searchQuery.trim()) {
      const filtered = availableItineraries.filter(itin => 
        itin.id.toString().includes(searchQuery) ||
        itin.itinerary_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredItineraries(filtered);
    } else {
      setFilteredItineraries(availableItineraries);
    }
  };

  const handleStep1Continue = () => {
    if (availableItineraries.length > 0 && selectedItineraries.length === 0) {
      alert('Please select at least one itinerary to complete the assignment.');
      return;
    }
    
    console.log('JB NEW 1🔥 CONTINUE TO STEP 2 BUTTON CLICKED');
    console.log('Selected itinerary IDs:', selectedItineraries);
    
    // Save to session storage
    const sessionKey = `assignment_${assignment.id}_selected_itineraries`;
    sessionStorage.setItem(sessionKey, JSON.stringify(selectedItineraries));
    
    // Prepare linked itineraries for step 2
    const linked = selectedItineraries.map(id => 
      availableItineraries.find(itin => itin.id === id)
    ).filter(Boolean);
    setLinkedItineraries(linked);
    
    setCurrentStep(2);
  };

  const handleBackToStep1 = () => {
    console.log('⬅️ BACK TO STEP 1 BUTTON CLICKED');
    const sessionKey = `assignment_${assignment.id}_selected_itineraries`;
    const savedSelection = sessionStorage.getItem(sessionKey);
    console.log('JB NEW 2 Back to Step 1 - Selected IDs from session:', savedSelection);
    
    setCurrentStep(1);
  };

  const handleDownload = async (itineraryId: number, isAgent = true) => {
    try {
      const fullItinerary = await itineraryService.getItinerary(itineraryId);
      const givenPrice = fullItinerary?.given_price;
      let hasSetPrice = false;
      
      if (givenPrice && typeof givenPrice === 'object') {
        hasSetPrice = Object.values(givenPrice).some((priceData: any) => 
          priceData && typeof priceData === 'object' && priceData.total && priceData.total > 0
        );
      }
      
      if (!hasSetPrice) {
        alert('Please set price before downloading');
        return;
      }
      
      if (isAgent) {
        await itineraryService.downloadAgentItinerary(itineraryId);
      } else {
        await itineraryService.downloadItinerary(itineraryId, false);
      }
      
      alert('Download started');
    } catch (error) {
      console.error('Error downloading:', error);
      alert('Failed to download');
    }
  };

  const handleFileUpload = async () => {
    if (uploadFiles.length === 0) return;
    
    try {
      // Get selected itineraries from session storage
      const sessionKey = `assignment_${assignment.id}_selected_itineraries`;
      const savedSelection = sessionStorage.getItem(sessionKey);
      
      if (!savedSelection) {
        alert('No itineraries selected. Please go back to step 1.');
        return;
      }
      
      const selectedIds = JSON.parse(savedSelection);
      const formData = new FormData();
      
      // Add selected itinerary IDs to the form data
      formData.append('selected_itinerary_ids', JSON.stringify(selectedIds));
      
      // Add files
      uploadFiles.forEach(file => {
        formData.append('files', file);
      });
      
      // Add completion notes if provided
      if (completionNotes.trim()) {
        formData.append('completion_notes', completionNotes.trim());
      }
      
      console.log('Uploading files for selected itineraries:', selectedIds);
      console.log('Files being uploaded:', uploadFiles.map(f => f.name));
      
      await api.post(`/work-assignments/${assignment.id}/complete`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Clear session storage on success
      sessionStorage.removeItem(sessionKey);
      
      onSuccess();
    } catch (error: any) {
      console.error('Error uploading files:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to upload files';
      alert(errorMessage);
    }
  };

  useEffect(() => {
    setFilteredItineraries(availableItineraries);
  }, [availableItineraries]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">
              {currentStep === 1 ? 'Step 1: Select Itineraries' : 'Step 2: Upload Files for Itineraries'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {currentStep === 1 ? (
            // STEP 1: Select Itineraries
            <>
              <p className="text-gray-600 mb-4">
                Select the itineraries you created for <strong>{assignment.agency_name}</strong>:
              </p>
              
              {availableItineraries.length > 0 && (
                <div className="mb-4">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Search by ID or name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      onClick={handleSearch}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                    >
                      Search
                    </button>
                  </div>
                </div>
              )}
              
              <div className="space-y-3 mb-6">
                {availableItineraries.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p className="mb-2">No unassigned itineraries found for {assignment.agency_name}.</p>
                    <p className="text-sm">Create itineraries first, then complete the assignment.</p>
                  </div>
                ) : (
                  filteredItineraries.map((itinerary) => (
                    <label key={itinerary.id} className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="mr-3 h-4 w-4 text-blue-600"
                        checked={selectedItineraries.includes(itinerary.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedItineraries([...selectedItineraries, itinerary.id]);
                          } else {
                            setSelectedItineraries(selectedItineraries.filter(id => id !== itinerary.id));
                          }
                        }}
                      />
                      <div className="flex-1">
                        <div className="flex items-center mb-1">
                          <span className="bg-indigo-600 text-white px-3 py-1.5 rounded text-base font-bold font-mono mr-3">WH_{itinerary.id}</span>
                          <div className="font-medium text-gray-900">{itinerary.itinerary_name}</div>
                        </div>
                        <div className="text-sm text-gray-500">
                          Status: {itinerary.status} • Updated: {new Date(itinerary.updated_at || itinerary.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '/')}
                        </div>
                      </div>
                    </label>
                  ))
                )}
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={onClose}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStep1Continue}
                  disabled={availableItineraries.length === 0}
                  className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue to Step 2 {availableItineraries.length > 0 ? `(${selectedItineraries.length} selected)` : ''}
                </button>
              </div>
            </>
          ) : (
            // STEP 2: Upload Files
            <>
              <p className="text-gray-600 mb-4">
                Upload DOCX/PDF files for each itinerary. Filename must contain the itinerary ID.
              </p>
              
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Linked Itineraries:</h3>
                <div className="space-y-4">
                  {linkedItineraries.map((itin) => (
                    <div key={itin.id} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center">
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-mono mr-2">#{itin.id}</span>
                          <span className="text-sm font-medium">{itin.itinerary_name}</span>
                        </div>
                        <button
                          onClick={() => handleDownload(itin.id)}
                          className="flex items-center px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs hover:bg-indigo-200"
                          title="Download DOCX"
                        >
                          📥 Download
                        </button>
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Upload file for this itinerary (DOCX/PDF)
                        </label>
                        <input
                          type="file"
                          accept=".docx,.pdf"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const newFiles = uploadFiles.filter((f: File) => !f.name.includes(itin.id.toString()));
                              newFiles.push(file);
                              setUploadFiles(newFiles);
                            }
                          }}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Filename must contain itinerary ID {itin.id} (e.g., "WH_{itin.id}.docx")
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {uploadFiles.length > 0 && (
                <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-800 mb-2">Files ready for upload:</h4>
                  <ul className="text-sm text-blue-700">
                    {uploadFiles.map((file: File, index: number) => (
                      <li key={index} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <span className="mr-2">📄</span>
                          {file.name}
                        </div>
                        <button
                          onClick={() => setUploadFiles(uploadFiles.filter((_, i) => i !== index))}
                          className="ml-2 text-red-600 hover:text-red-800 text-xs"
                          title="Remove file"
                        >
                          ✕
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Completion Notes */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Completion Notes (Optional)
                </label>
                <textarea
                  value={completionNotes}
                  onChange={(e) => setCompletionNotes(e.target.value)}
                  placeholder="Add any final details, changes, or communication for the agency..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  These notes will be visible to the agency and can include any important details about the itinerary.
                </p>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={handleBackToStep1}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Back to Step 1
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleFileUpload}
                  disabled={uploadFiles.length === 0}
                  className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Complete Assignment ({uploadFiles.length} files)
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignmentCompletionWizard;